/**
 * Post-login return path (tech-survey AC-ERROR-7).
 *
 * `ProtectedRoute` records where a signed-out visitor was heading before it
 * redirects to /login; `LoginPage` sends them there after authentication
 * (password, new-password step, or the SSO full-page round trip — which is why
 * this lives in same-tab `sessionStorage`, like the pending federation email).
 *
 * Only same-origin RELATIVE paths are ever stored or returned, so a crafted
 * link can never turn the login redirect into an open redirect. With nothing
 * stored, callers fall back to "/" exactly as before.
 */

export const POST_LOGIN_PATH_KEY = 'banyan_post_login_path';

/** A remembered destination older than this is ignored (abandoned login attempts). */
const MAX_AGE_MS = 30 * 60 * 1000;

// Any ASCII control character (includes \t \n \r), which browsers strip inside URLs.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/**
 * Returns `raw` when it is a safe same-origin relative path, else null.
 * "/login" is excluded: returning to it would loop.
 */
export function sanitizePostLoginPath(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw === '') return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  if (CONTROL_CHARS.test(raw)) return null;

  // Belt and braces: it must still resolve to our own origin.
  try {
    const base = 'http://post-login.invalid';
    if (new URL(raw, base).origin !== base) return null;
  } catch {
    return null;
  }

  const pathname = raw.split(/[?#]/, 1)[0];
  if (pathname === '/login' || pathname === '/login/') return null;
  return raw;
}

export interface LocationLike {
  pathname: string;
  search?: string;
  hash?: string;
}

// An explicit logout also lands on /login through ProtectedRoute; the page the
// user logged out from must not become the NEXT login's destination.
let captureSuppressed = false;

/** Called by logout: forget any remembered path and stop capturing until `resumePostLoginCapture`. */
export function suppressPostLoginCapture(): void {
  captureSuppressed = true;
  clearPostLoginPath();
}

/** Called once an authenticated visit has arrived: capturing resumes. */
export function resumePostLoginCapture(): void {
  captureSuppressed = false;
}

/** Remember the destination. No-op for "/" (the default anyway), unsafe paths and after a logout. */
export function savePostLoginPath(location: LocationLike): void {
  if (captureSuppressed) return;
  const path = sanitizePostLoginPath(
    `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`
  );
  if (path === null || path === '/') return;
  try {
    sessionStorage.setItem(POST_LOGIN_PATH_KEY, JSON.stringify({ path, savedAt: Date.now() }));
  } catch {
    /* ignore — private mode / storage unavailable; login lands on "/" */
  }
}

/** The remembered destination, re-validated on read; null when absent, stale or tampered with. */
export function peekPostLoginPath(): string | null {
  try {
    const stored = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { path, savedAt } = parsed as { path?: unknown; savedAt?: unknown };
    if (typeof savedAt !== 'number' || Date.now() - savedAt > MAX_AGE_MS) return null;
    return sanitizePostLoginPath(path);
  } catch {
    return null;
  }
}

export function clearPostLoginPath(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
  } catch {
    /* ignore */
  }
}
